import { asyncRoutes, constantRoutes } from '@/router'

const localRouteFallbackMap = {
}

const routeTitleFallbackMap = {
  Dashboard: '仪表盘',
  LogManagement: '日志管理',
  LogUserStatus: '用户状态',
  LogLogin: '登录日志',
  LogOperation: '操作日志',
  LogSession: '会话日志',
  SystemConfig: '系统管理',
  UserManagement: '后台账号',
  SystemBasicConfig: '系统设置',
  RoleManagement: '角色管理',
  MenuManagement: '菜单管理',
  DictionaryManagement: '字典管理',
  PermissionSettings: '权限管理',
  ScheduledTaskManagement: '任务管理',
  SystemPassword: '密码修改',
  UserManagementRoot: '用户管理',
  UserListManagement: '用户列表',
  NoiseManagement: '噪音管理',
  NoiseDeviceManagement: '设备管理',
  NoiseEventManagement: '噪音事件',
  NoiseReportManagement: '报告管理',
}

function normalizeRouteTitle(title, routeName) {
  const text = String(title || '').trim()
  if (routeTitleFallbackMap[routeName]) {
    return routeTitleFallbackMap[routeName]
  }
  return text
}

function getEffectiveRouteName(route) {
  if (route.name) {
    return route.name
  }
  if (route.children && route.children.length === 1) {
    return getEffectiveRouteName(route.children[0])
  }
  return ''
}

function getMenuSort(route, menuMap) {
  const routeName = getEffectiveRouteName(route)
  if (!routeName || !menuMap[routeName]) {
    return Number.MAX_SAFE_INTEGER
  }
  return typeof menuMap[routeName].sort === 'number' ? menuMap[routeName].sort : Number.MAX_SAFE_INTEGER
}

function sortRoutes(routes, menuMap) {
  return [...routes]
    .map(route => {
      const sortedRoute = { ...route }
      if (sortedRoute.children && sortedRoute.children.length) {
        sortedRoute.children = sortRoutes(sortedRoute.children, menuMap)
      }
      return sortedRoute
    })
    .sort((a, b) => {
      const sortA = getMenuSort(a, menuMap)
      const sortB = getMenuSort(b, menuMap)
      if (sortA !== sortB) {
        return sortA - sortB
      }
      return (a.path || '').localeCompare(b.path || '')
    })
}

function hasPermissionAccess(route, permissionCodeSet) {
  const requiredPermission = route.meta && route.meta.permission
  if (!requiredPermission) {
    return true
  }
  return permissionCodeSet.has(requiredPermission)
}

function hasMenuAccess(route, routeNameSet, menuMap, permissionCodeSet) {
  const routeName = getEffectiveRouteName(route)
  if (!hasPermissionAccess(route, permissionCodeSet)) {
    return false
  }
  if (!routeName) {
    return true
  }
  const menu = menuMap[routeName]
  return (routeNameSet.has(routeName) || localRouteFallbackMap[routeName]) && (!menu || menu.visible !== false)
}

function patchRouteMeta(route, menuMap) {
  const routeName = getEffectiveRouteName(route)
  const currentMeta = route.meta || {}
  const currentTitle = normalizeRouteTitle(currentMeta.title, routeName)

  if (routeName && menuMap[routeName]) {
    const menu = menuMap[routeName]
    route.meta = {
      ...currentMeta,
      title: normalizeRouteTitle(menu.title, routeName) || currentTitle || routeTitleFallbackMap[routeName] || '',
      icon: menu.icon || currentMeta.icon
    }
    route.hidden = menu.visible === false
  } else if (routeName) {
    route.meta = {
      ...currentMeta,
      title: currentTitle || routeTitleFallbackMap[routeName] || ''
    }
  }
  return route
}

function mapRoutes(routes, menuMap) {
  return sortRoutes(routes, menuMap).map(route => {
    const mappedRoute = patchRouteMeta({ ...route }, menuMap)
    if (mappedRoute.children) {
      mappedRoute.children = mapRoutes(mappedRoute.children, menuMap)
    }
    return mappedRoute
  })
}

function filterAsyncRoutes(routes, routeNameSet, menuMap, permissionCodeSet) {
  return routes.reduce((acc, route) => {
    const current = { ...route }
    if (current.children && current.children.length) {
      current.children = filterAsyncRoutes(current.children, routeNameSet, menuMap, permissionCodeSet)
    }
    if (hasMenuAccess(current, routeNameSet, menuMap, permissionCodeSet) || (current.children && current.children.length)) {
      acc.push(current)
    }
    return acc
  }, [])
}

const state = {
  routes: [],
  addRoutes: []
}

const mutations = {
  SET_ROUTES(state, routes) {
    state.addRoutes = routes
    state.routes = constantRoutes.concat(routes)
  }
}

const actions = {
  generateRoutes({ commit }, payload = {}) {
    const menus = payload.menus || []
    const permissionCodes = payload.permissionCodes || []
    const permissionCodeSet = new Set(permissionCodes)
    const routeNameSet = new Set(menus.map(menu => menu.route_name).filter(Boolean))
    const menuMap = menus.reduce((acc, menu) => {
      acc[menu.route_name] = menu
      return acc
    }, {})

    const filteredAsyncRoutes = filterAsyncRoutes(asyncRoutes, routeNameSet, menuMap, permissionCodeSet)
    const accessedRoutes = mapRoutes(filteredAsyncRoutes, menuMap)
    commit('SET_ROUTES', accessedRoutes)
    return accessedRoutes
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
